import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MediaManagementService } from '../../../../services/media-management.service';
import { InlineEditSessionV2 } from './inline-edit-session-v2.service';

/** The same file types app-image-upload's picker offers. */
const IMAGE_ACCEPT = 'image/jpg, image/jpeg, image/png, image/gif, image/bmp, image/tif, image/tiff';

/**
 * Turns its host into an in-canvas image slot, as in the prototype: clicking
 * it (or Enter / Space) opens the file picker, and an image file can be
 * dropped onto it. The picked file is stored exactly like the right panel's
 * app-image-upload stores it (MediaManagementService.storeImage), and the new
 * URI is emitted through `inlineMediaChange` - the block wires that to its
 * existing onChange, so canvas uploads take the same blockData path as
 * right-panel ones.
 *
 * Like [appInlineEdit], it only acts while the surrounding InlineEditSessionV2
 * is enabled (Edit mode); otherwise clicks fall through, so a linked image
 * opens its link in View mode. Without a session the host stays inert.
 *
 * Styles (pointer, empty upload card, hover / drag-over) come from v2-shared's
 * `inline-media` mixin.
 */
@Directive({
  selector: '[appInlineMedia]',
  standalone: true,
  host: {
    class: 'cr-inline-media',
    '[class.cr-inline-media-empty]': '!value',
  },
})
export class InlineMediaV2Directive implements OnInit, OnChanges, OnDestroy {
  /** The image the slot currently shows - replaced (and queued for deletion
   *  on save) when a new one is picked. */
  @Input('appInlineMedia') value: string | null | undefined = '';
  @Input() inlineMediaBlockId = '';

  @Output() inlineMediaChange = new EventEmitter<string>();

  private editable = false;
  private fileInput?: HTMLInputElement;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private host: ElementRef<HTMLElement>,
    private mediaManagementService: MediaManagementService,
    @Optional() private session: InlineEditSessionV2 | null,
  ) {}

  ngOnInit(): void {
    this.session?.enabled$.pipe(takeUntil(this.destroy$)).subscribe((enabled) => this.setEditable(enabled));
  }

  ngOnChanges(): void {
    this.updateLabel();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Opens the picker instead of following the link around the image. */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.editable) {
      return;
    }
    event.preventDefault();
    this.openPicker();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.editable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.openPicker();
    }
  }

  /** The image (and the link around it) would drag itself around the canvas. */
  @HostListener('dragstart', ['$event'])
  onDragStart(event: DragEvent): void {
    if (this.editable) {
      event.preventDefault();
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (!this.editable || !event.dataTransfer?.types.includes('Files')) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    this.setDragOver(true);
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    // Moving between the host's own children isn't leaving it.
    if (!this.host.nativeElement.contains(event.relatedTarget as Node | null)) {
      this.setDragOver(false);
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (!this.editable) {
      return;
    }
    event.preventDefault();
    this.setDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (file?.type.startsWith('image/')) {
      this.session?.focus(this.inlineMediaBlockId);
      this.store(file);
    }
  }

  private openPicker(): void {
    this.session?.focus(this.inlineMediaBlockId);
    // Kept out of the DOM: inside the host, its own click would bubble back
    // into onClick.
    if (!this.fileInput) {
      this.fileInput = document.createElement('input');
      this.fileInput.type = 'file';
      this.fileInput.accept = IMAGE_ACCEPT;
      this.fileInput.addEventListener('change', () => {
        const file = this.fileInput!.files?.[0];
        // Reset, so picking the same file again still fires `change`.
        this.fileInput!.value = '';
        if (file) {
          this.store(file);
        }
      });
    }
    this.fileInput.click();
  }

  private store(file: File): void {
    const blockId = this.inlineMediaBlockId;
    this.mediaManagementService
      .storeImage(file, blockId, this.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (uri) => {
          this.inlineMediaChange.emit(uri);
          // The block's right-panel form only reads its image when it's created.
          this.session?.commit(blockId);
        },
        error: (err) => {
          console.error('Storing the image failed:', err);
        },
      });
  }

  private setEditable(enabled: boolean): void {
    const el = this.host.nativeElement;
    this.editable = enabled;
    el.classList.toggle('is-editable', enabled);
    if (enabled) {
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
    } else {
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      this.setDragOver(false);
    }
    this.updateLabel();
  }

  private updateLabel(): void {
    const el = this.host.nativeElement;
    if (this.editable) {
      el.setAttribute('aria-label', this.value ? 'Replace image' : 'Upload image');
    } else {
      el.removeAttribute('aria-label');
    }
  }

  private setDragOver(over: boolean): void {
    this.host.nativeElement.classList.toggle('is-drag-over', over);
  }
}
